import { Controller } from "@hotwired/stimulus"
import * as questions from "../libs/curriculum_questions"
import { enter, reducedMotion, spring } from "../libs/motion"

// Walks a teacher through curriculum › stage › subject › strand › part › outcome one
// question at a time, one answer each: picking moves straight on, except on the last
// question, where "Show x lessons" folds the panel. A question with a single answer is
// answered and skipped for them. The answers go with the Library's filters form, so the
// lessons below are filtered on the server as they change.
export default class extends Controller {
  static targets = ["panel", "summary", "summaryText", "step", "group", "groupTitle",
                    "progress", "actions", "back", "forward", "show", "count", "dropdown", "more", "smartNote", "smartText", "frame"]
  static values = { url: String }

  connect() {
    this.element.addEventListener("change", event => {
      if (!this.hasPanelTarget) return
      this.yearsChanged(event)
      this.update()
    })
  }

  // The lessons below are the server's answer to the picks; asked again only when they change.
  submit() {
    const answers = this.picked.map(input => `${input.name}=${input.value}`).join("&")
    if (this.submitted === undefined) this.submitted = answers
    if (answers === this.submitted) return

    this.submitted = answers
    clearTimeout(this.submitting)
    this.submitting = setTimeout(() => document.getElementById("library-filters")?.requestSubmit(), 250)
  }

  // New results came back: Show x lessons counts them.
  countTargetConnected() {
    if (this.hasShowTarget) this.showTarget.querySelector("span").textContent = this.showLabel()
  }

  // Results refresh in their own frame, so the panel stays put and keeps the teacher's
  // place. It remembers where they were in case it's connected again; a page that comes
  // with answers (from the filters window, or a shared link) starts from those.
  panelTargetConnected() {
    this.loading = false
    const history = this.panelTarget.dataset.history
    const picked = this.panelTarget.querySelector("[data-key='curriculum'] input:checked")
    if (!history || !picked) return this.restart()

    this.history = JSON.parse(history)
    this.index = this.history[this.history.length - 1]
    this.startFromYearLevels()
    this.update()
  }

  // The curriculum chip and the first question are the same answer. Picking
  // one in the chip starts the flow at the question after it.
  fromDropdown({ target }) {
    if (target.checked) this.dropdownOptions.forEach(option => { if (option !== target) option.checked = false })
    this.dropdownOptions.forEach(option => {
      const answer = this.panelTarget.querySelector(`[data-key='curriculum'] input[value='${option.value}']`)
      if (answer) answer.checked = option.checked
    })
    const picked = this.dropdownOptions.find(option => option.checked)?.value
    if (picked && picked !== this.panelTarget.dataset.curriculum) return this.load(picked)

    this.restart()
  }

  // The address carries the answers, so a reload, a shared link or the filters
  // modal opens on them. Each answer's input is named for its place there. The
  // year levels go too: picking a stage can take them off.
  rememberAnswers() {
    const url = new URL(location.href)
    ;[...url.searchParams.keys()].filter(key => key === "curriculum" || key.startsWith("guide[") || key === "year_level[]").forEach(key => url.searchParams.delete(key))
    this.picked.forEach(input => url.searchParams.set(input.name, input.value))
    questions.pickedYears(this.element).forEach(year => url.searchParams.append("year_level[]", year))
    history.replaceState(history.state, "", url)
  }

  // Only the picked curriculum's questions are on the page. Another one's load
  // in their place, and the panel starts over when they arrive.
  load(curriculum) {
    const address = new URL(location.href)
    ;[...address.searchParams.keys()].filter(key => key.startsWith("guide[")).forEach(key => address.searchParams.delete(key))
    address.searchParams.set("curriculum", curriculum)
    history.replaceState(history.state, "", address)

    const url = new URL(this.urlValue, location.origin)
    url.searchParams.set("curriculum", curriculum)
    this.loading = true
    this.heightBeforeLoad = this.panelTarget.offsetHeight
    this.frameTarget.src = url.href
  }

  restart() {
    this.index = 0
    this.history = [0]
    this.hint = null
    this.cascade()
    const picked = this.panelTarget.querySelector("[data-key='curriculum'] input:checked")
    this.summaryTarget.classList.replace("flex", "hidden")
    this.panelTarget.classList.toggle("hidden", !picked)
    this.panelTarget.classList.toggle("flex", !!picked)
    if (!picked) {
      delete this.panelTarget.dataset.history
      return this.update()
    }

    this.next()
    if ("applied" in this.panelTarget.dataset) return this.resume()
    if (this.startFromYearLevels()) this.next()
  }

  // Answers that came with the page (from the filters modal) walk straight
  // through to the summary; Edit picks up at the last of them.
  resume() {
    delete this.panelTarget.dataset.applied
    while (this.panelTarget.classList.contains("flex") && this.stepTargets[this.index].querySelector("input:checked")) this.next()
    if (this.panelTarget.classList.contains("flex")) this.finish()
  }

  // The Year level filter answers the stage question when it points to one stage.
  startFromYearLevels() {
    const { hint, kind } = questions.followYears(this.element, this.stageOptions())
    this.showHint(hint, kind)
    return this.stageOptions().some(stage => stage.checked && stage.dataset.fromYears)
  }

  // Changing the year levels keeps the stage in step, and goes back to the stage
  // question when its answer changed.
  yearsChanged({ target, detail }) {
    if (target.name !== "year_level[]" || detail?.fromStage) return
    if (!this.panelTarget.querySelector("[data-key='curriculum'] input:checked")) return

    const { hint, kind, changed } = questions.followYears(this.element, this.stageOptions())
    this.showHint(hint, kind)
    if (!changed) return

    const stageIndex = this.stepTargets.findIndex(step => step.dataset.key === "stage")
    this.history = this.history.filter(i => i < stageIndex).concat(stageIndex)
    this.index = stageIndex
    this.cascade()
    if (this.stageOptions().some(stage => stage.checked)) this.next()
  }

  // A hint shows once, on the question the teacher lands on after the change, and
  // goes once they answer it. One saying the stage came from their year levels
  // shows again whenever they're back on the stage question.
  showHint(text, kind) {
    this.hint = text ? { text, kind, at: null } : null
  }

  answeredHint() {
    if (this.hint.kind === "based") this.hint.at = -1
    else this.hint = null
  }

  stageOptions() {
    const step = this.stepTargets.find(step => step.dataset.key === "stage")
    return step ? this.visibleOptions(step) : []
  }

  // One answer per question: picking moves on after a beat, except on the last
  // question. Clicking the answer you already have keeps it and carries on.
  pick({ target }) {
    if (target.tagName !== "INPUT" || !target.closest("[data-curriculum-guide-target='step']")) return

    const step = this.stepTargets[this.index]
    if (!step.contains(target)) return
    target.checked = true
    step.querySelectorAll("input:checked").forEach(input => { if (input !== target) input.checked = false })
    if (step.dataset.key === "curriculum" && target.value !== this.panelTarget.dataset.curriculum) {
      this.syncDropdown()
      return this.load(target.value)
    }
    if (step.dataset.key === "stage") this.showHint(questions.removeClashingYears(this.element, target), "removed")
    else if (this.hint?.at === this.index) this.answeredHint()
    clearTimeout(this.advance)
    this.advance = this.isFinalStep() ? null : setTimeout(() => this.next(), 200)
    this.update()
  }

  toggleMore({ currentTarget: button }) {
    const from = this.panelTarget.offsetHeight
    questions.toggleMore(this.moreTargets.filter(row => button.parentElement.contains(row)))
    questions.updateMoreButtons(this.moreTargets)
    if (!reducedMotion()) spring(this.panelTarget, from, this.panelTarget.offsetHeight)
  }

  revealPicked() {
    questions.revealPicked(this.moreTargets)
    questions.updateMoreButtons(this.moreTargets)
  }

  next() {
    clearTimeout(this.advance)
    this.advance = null
    let index = this.index + 1
    while (index < this.stepTargets.length) {
      const options = this.visibleOptions(this.stepTargets[index])
      if (options.length > 1) break
      if (options.length === 1) {
        options[0].checked = true
        this.cascade()
      }
      index++
    }
    if (index >= this.stepTargets.length) return this.finish()

    this.index = index
    this.history.push(index)
    this.update()
  }

  back() {
    if (this.history.length < 2) return
    clearTimeout(this.advance)
    this.advance = null
    this.history.pop()
    this.index = this.history[this.history.length - 1] ?? 0
    this.update()
  }

  // The folded summary reopens where the teacher left off; its own buttons
  // (Edit, each part of the sentence) handle their click themselves.
  open(event) {
    if (!event.target.closest("button, a")) this.edit(event)
  }

  // A click anywhere else folds the panel. The Curriculum filter that drives
  // it and the folded summary (whose Edit reopens it) don't count.
  clickOutside({ target }) {
    if (!this.panelTarget.classList.contains("flex") || !target.isConnected) return
    if ([ this.panelTarget, this.summaryTarget, this.dropdownTarget ].some(area => area.contains(target))) return

    this.collapse()
  }

  // Folds the panel into its summary sentence at any point; Edit picks up
  // at the same question.
  collapse() {
    if (this.panelTarget.classList.contains("flex")) this.finish()
  }

  finish() {
    this.update()
    this.morph(this.panelTarget, this.summaryTarget)
  }

  // Each part of the summary sentence reopens the question it answered.
  edit(event) {
    const key = event.currentTarget.dataset.key
    const index = key ? this.stepTargets.findIndex(step => step.dataset.key === key) : this.history[this.history.length - 1]
    this.history = this.history.filter(i => i < index).concat(index)
    this.index = index
    this.update()
    this.morph(this.summaryTarget, this.panelTarget)
  }

  // Opening and folding: one box springs to the other's height as its content rises in, one after another.
  morph(from, to) {
    const height = from.offsetHeight
    from.classList.replace("flex", "hidden")
    to.classList.replace("hidden", "flex")
    if (reducedMotion()) return

    spring(to, height, to.offsetHeight)
    ;[...to.children].forEach((child, i) => enter(child, "translateY(8px) scale(0.98)", i * 40))
  }

  // Until another curriculum's questions arrive, the panel holds still: redrawn now,
  // it would show the first question as if it were the last, with no lessons.
  update() {
    if (this.loading) return

    const switching = this.shownIndex !== undefined && this.shownIndex !== this.index && this.panelTarget.classList.contains("flex")
    // The new curriculum's panel springs from the old one's height, not its own empty frame.
    const from = switching ? (this.heightBeforeLoad ?? this.panelTarget.offsetHeight) : 0
    this.heightBeforeLoad = null
    this.cascade()
    this.syncDropdown()
    this.revealPicked()

    this.stepTargets.forEach((step, i) => {
      step.classList.toggle("hidden", i !== this.index)
      step.classList.toggle("flex", i === this.index)
      const groups = this.visibleGroups(step)
      groups.forEach(group => group.querySelector("[data-curriculum-guide-target='groupTitle']")?.classList.toggle("hidden", groups.length < 2))
    })

    const stageIndex = this.stepTargets.findIndex(step => step.dataset.key === "stage")
    if (this.hint?.at === null && (this.index > stageIndex || (this.hint.kind === "cleared" && this.index === stageIndex))) this.hint.at = this.index
    const hinting = this.hint && (this.index === this.hint.at || (this.hint.kind === "based" && this.index === stageIndex))
    this.smartNoteTarget.hidden = !hinting
    this.smartTextTarget.textContent = hinting ? this.hint.text : ""

    this.panelTarget.dataset.history = JSON.stringify(this.history)
    this.rememberAnswers()
    this.progressTarget.querySelector("[role='progressbar'] > div")?.style.setProperty("width", `${((this.index + 1) / this.stepTargets.length) * 100}%`)
    const final = this.isFinalStep()
    this.backTarget.querySelector("button").setAttribute("aria-disabled", this.history.length < 2)
    this.forwardTarget.classList.toggle("invisible", final || this.advance || !this.stepTargets[this.index].querySelector("input:checked"))
    this.actionsTarget.hidden = !final

    this.submit()
    this.summaryTextTarget.innerHTML = this.sentence()
    if (switching) this.ease(from, Math.sign(this.index - this.shownIndex))
    this.shownIndex = this.index
  }

  // The next question slides in from the way you're going while the panel springs to its new height.
  ease(from, direction) {
    if (reducedMotion()) return

    enter(this.stepTargets[this.index], `translateX(${direction * 16}px)`)
    spring(this.panelTarget, from, this.panelTarget.offsetHeight)
  }

  // The last question: no question after it has a choice to make, looking
  // ahead the way the flow walks (a one-option question is answered on the way).
  isFinalStep() {
    if (this.index === this.stepTargets.length - 1) return true
    if (!this.stepTargets[this.index].querySelector("input:checked")) return false

    const borrowed = []
    let last = true
    for (const step of this.stepTargets.slice(this.index + 1)) {
      const options = this.visibleOptions(step)
      if (options.length > 1) { last = false; break }
      if (options.length === 1 && !options[0].checked) {
        options[0].checked = true
        borrowed.push(options[0])
      }
    }
    borrowed.forEach(input => { input.checked = false })
    this.cascade()
    return last
  }

  // The count of the lessons the server last showed for these answers.
  showLabel() {
    const count = Number(this.countTarget.dataset.count)
    return questions.format(questions.plural(questions.copy().show_lessons, count), { count })
  }

  // Unpicking an answer unpicks everything that depended on it.
  cascade() {
    questions.cascade(this.panelTarget, this.groupTargets)
  }

  get picked() {
    return [...this.panelTarget.querySelectorAll("[data-curriculum-guide-target='step'] input:checked")]
  }

  // "Lessons for years 7–8 covering mental health: “Analyse factors…”", each part reopening its question.
  // "General" and "Other" only collect what sits outside an area, so they're left out.
  sentence() {
    const { summary } = questions.copy()
    const labels = key => [...this.element.querySelectorAll(`[data-key='${key}'] input:checked`)].map(input => input.closest("label").dataset)
      .filter(label => !("catchAll" in label)).map(label => label.label)
    const join = list => list.length > 2 ? `${list.slice(0, 2).join(", ")} ${questions.format(summary.more, { count: list.length - 2 })}` : list.join(questions.copy().and)
    const escape = text => text.replace(/[&<>"]/g, char => `&#${char.charCodeAt(0)};`)
    const part = (text, key, title = text) => `<a href="#" title="${escape(title)}" class="font-medium text-content-primary underline decoration-contrast-high underline-offset-4 hover:decoration-content-primary" data-action="curriculum-guide#edit:prevent" data-key="${key}">${escape(text)}</a>`
    const year = labels("stage").map(questions.midSentence)
    const subject = labels("subject")
    const areas = labels("strand")
    const parts = labels("part")
    const outcomes = labels("outcome")
    let text = questions.format(escape(summary.lessons_for), { who: year.length ? part(join(year), "stage") : part(join(labels("curriculum")), "curriculum") })
    if (subject.length) text += ` · ${part(join(subject), "subject")}`
    if (areas.length) text += questions.format(escape(summary.covering), { areas: part(join(areas), "strand") })
    if (parts.length) text += ` · ${part(join(parts), "part")}`
    if (outcomes.length) text += questions.format(escape(summary.outcome), { outcome: part(this.shorten(outcomes[0]), "outcome", outcomes[0]) })
    return text
  }

  // The outcome's own words, cut at a word after about 60 characters; the link's title holds the rest.
  shorten(text, length = 60) {
    if (text.length <= length) return text
    const cut = text.lastIndexOf(" ", length)
    return `${text.slice(0, cut > 0 ? cut : length).replace(/[,;:]$/, "")}…`
  }

  visibleGroups(step) {
    return [...step.querySelectorAll("[data-curriculum-guide-target='group']")].filter(group => !group.classList.contains("hidden"))
  }

  visibleOptions(step) {
    this.cascade()
    return questions.visibleOptions(step, this.groupTargets)
  }

  // The menu repeats the picked option as a copy at the top; the answer lives
  // on the real options below it.
  get dropdownOptions() {
    return [...this.dropdownTarget.querySelectorAll("[role='menu'] input")].filter(option => !option.closest("[data-select-filter-target='picked']"))
  }

  syncDropdown() {
    this.panelTarget.querySelectorAll("[data-key='curriculum'] input").forEach(answer => {
      const option = this.dropdownOptions.find(option => option.value === answer.value)
      if (option) option.checked = answer.checked
    })
    this.application.getControllerForElementAndIdentifier(this.dropdownTarget, "select-filter")?.refresh()
  }
}
